import { createStore } from 'vuex'
import axios from 'axios'
import { AuthService } from '@/utils/auth.js'
import { fetchCurrentUser as fetchCurrentUserApi, loginWithPassword, logoutOAuth } from '@/utils/oauth.js'

const normalizeUser = (user = {}) => ({
  ...user,
  uuid: user.uuid || user.sub || user.userId || user.id,
  userId: user.userId || user.uuid || user.sub || user.id,
  id: user.id || user.uuid || user.sub || user.userId,
  userName: user.userName || user.username || user.name || user.email || '用户',
  handle: user.handle || user.email || user.uuid || user.sub || '',
  avatarUrl: user.avatarUrl || user.avatar_url || ''
})

const persistAuth = ({ user, expiresIn, expires }) => {
  const normalized = normalizeUser(user)
  localStorage.setItem('user', JSON.stringify(normalized))
  if (expires) {
    localStorage.setItem('expires', expires)
  } else if (expiresIn) {
    localStorage.setItem('expires', new Date(Date.now() + expiresIn * 1000).toISOString())
  }
  return normalized
}

export default createStore({
  state: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: '',
    expires: localStorage.getItem('expires') || '',

    // 全局语言（繁简）
    locale: localStorage.getItem('locale') || 'zh-TW',

    // 搜索状态
    chatStorage: [{ role: 'assistant', content: '已找到相关资料，正在为您分析...' }],
    sources: [],
    fullContext: '',
    currentTopic: '',
    initialQuery: ''
  },
  getters: {
    isLoggedIn: state => state.user && state.user.userId !== 'guest',
    currentUser: state => state.user,
    isTraditional: state => state.locale === 'zh-TW'
  },
  mutations: {
    SET_USER(state, user) {
      const userData = normalizeUser(user)
      localStorage.setItem('user', JSON.stringify(userData))
      state.user = userData
    },
    SET_TOKEN(state, token) {
      state.token = token || ''
      localStorage.removeItem('token')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    },
    SET_EXPIRES(state, expires) {
      state.expires = expires
      localStorage.setItem('expires', expires)
    },
    LOGOUT(state) {
      state.user = null
      state.token = ''
      state.expires = ''

      localStorage.removeItem('user')
      localStorage.removeItem('expires')
      localStorage.removeItem('userId')
    },
    SET_LOCALE(state, locale) {
      state.locale = locale === 'zh-CN' ? 'zh-CN' : 'zh-TW'
      localStorage.setItem('locale', state.locale)
    },

    // 更新搜索结果
    SET_SEARCH_RESULT(state, { sources, context }) {
      state.sources = sources || []
      state.fullContext = context || ''
    },
    // 添加聊天消息
    ADD_CHAT_MESSAGE(state, message) {
      state.chatStorage.push(message)
    },
    // 重置搜索会话
    RESET_SEARCH_SESSION(state) {
      state.chatStorage = [{ role: 'assistant', content: '已找到相关资料，正在为您分析...' }]
      state.sources = []
      state.fullContext = ''
      state.currentTopic = ''
      state.initialQuery = ''
    },
    UPDATE_LAST_MESSAGE(state, chunk) {
      state.chatStorage[state.chatStorage.length - 1].content += chunk;
    },
    SET_CURRENT_TOPIC(state, topic) {
      state.currentTopic = topic
    },
    SET_INITIAL_QUERY(state, query) {
      state.initialQuery = query
    }
  },
  actions: {
    async login({ commit }, { email, password }) {
      try {
        const data = await loginWithPassword({ email, password })
        const user = data.user
        const expires = data.expires_in || data.expires

        persistAuth({
          user,
          expiresIn: typeof expires === 'number' ? expires : undefined,
          expires: typeof expires === 'string' ? expires : undefined
        })
        commit('SET_USER', user)
        commit('SET_TOKEN', '')
        if (typeof expires === 'number') {
          commit('SET_EXPIRES', new Date(Date.now() + expires * 1000).toISOString())
        } else if (typeof expires === 'string') {
          commit('SET_EXPIRES', expires)
        }

        return true
      } catch (err) {
        console.error('登录失败', err)
        return false
      }
    },
    async completeOAuthLogin({ commit }, tokenData) {
      const user = tokenData.user
      persistAuth({
        user,
        expiresIn: tokenData.expires_in
      })
      commit('SET_USER', user)
      commit('SET_TOKEN', '')
      if (tokenData.expires_in) {
        commit('SET_EXPIRES', new Date(Date.now() + tokenData.expires_in * 1000).toISOString())
      }
      return true
    },
    async fetchCurrentUser({ commit }) {
      const data = await fetchCurrentUserApi()
      const user = data.user || data
      persistAuth({ user })
      commit('SET_USER', user)
      commit('SET_TOKEN', '')
      return user
    },
    async logout({ commit }) {
      let logoutUrl = '';
      try {
        const data = await logoutOAuth();
        logoutUrl = data?.oauth_logout_url || '';
      } catch (error) {
        try {
          const res = await axios.post('/api/logout');
          logoutUrl = res.data?.oauth_logout_url || '';
        } catch (legacyError) {
          console.error("后端清除Cookie失败:", legacyError);
        }
      }
      AuthService.clearLoginData()
      commit('LOGOUT');

      if (logoutUrl) {
        window.location.href = logoutUrl;
      }

      return logoutUrl;
    },

    // 处理搜索 API 调用
    async fetchSearchResult({ commit, state }, question) {
      try {
        const params = {
          topic_name: state.currentTopic.replace('@', ''),
          q: question
        }

        const res = await axios.get('/api/search/agent', { params })

        commit('SET_SEARCH_RESULT', {
          sources: res.data.sources,
          context: res.data.context
        })
      } catch (err) {
        console.error("加载搜索结果失败", err)
      }
    }
  },
  modules: {}
})
